<template>
  <NdsDlg
    v-if="selectedApplic"
    :isOpen.sync="isNdsDlgOpen"
    :application="selectedApplic"
    :offer-price-with-nds="selectedApplic.offerPriceWithNds"
    @update:isOpen="val => {
      isNdsDlgOpen = val;
      if (!val) selectedApplic = null;
    }"
  />

  <Card>
    <v-card-title class="cardTitle pa-0">
      {{ $t(title) }}
    </v-card-title>
    <div class="d-flex justify-space-between">
      <v-card-subtitle class="cardSubTitle px-0 mt-2">
        {{ $t(subtitle) }}
      </v-card-subtitle>
      <div class="d-flex flex-row ga-2">
        <v-switch
          v-if="decisionGridStore.hideNds === false && applications.length > 0"
          :label="$t('Показать цены без НДС')"
          hide-details
          inset
          class="mr-4"
          :model-value="isEnabled"
          @update:model-value="val => isEnabled = val"
        ></v-switch>
        <component
          v-if="decisionGridActionsComponent"
          :is="decisionGridActionsComponent"
          :procedureId="procedureId"
          :applications="applications"
          :status="status"
        />
        <v-btn
          class="resetButton px-5"
          @click="() => setAllTo()"
          color="primary"
          variant="outlined"
          flat
        >{{ $t('Сбросить выбор') }}</v-btn>
      </div>
    </div>
    <v-row>
      <v-col cols="12" sm="12" md="12">
        <DataTable
          :headers="headers"
          :items="applications"
          loading-text="Загрузка данных..."
          no-data-text="Нет заявок"
          :items-per-page="-1"
          :disable-sort="true"
          class="applicationsGrid"
        >
          <template #bottom></template>
          <template v-slot:[`item.${companyKey}`]="{ item }">
            {{ item[companyKey] }} <v-icon size="26" color="blue">mdi-check-decagram</v-icon>
          </template>
          <template v-slot:item.offerPrice="{ item }">
            <div v-if="hasManyRounds" class="d-flex align-center">
              <div class="offerPriceArrow">
                <v-icon
                  v-if="item.previous?.offerPrice !== null && Number(item.offerPrice) < Number(item.previous?.offerPrice)"
                  size="medium"
                >
                  custom:offerPriceArrowIcon
                </v-icon>
                <v-icon
                  v-if="item.previous?.offerPrice !== null && Number(item.offerPrice) > Number(item.previous?.offerPrice)"
                  size="medium"
                >
                  custom:offerPriceArrowRaiseIcon
                </v-icon>
              </div>
              <div class="offerPrice d-flex flex-column">
                <span
                  :style="{ cursor: item.nds.rate ? 'pointer' : 'default' }"
                  @click="item.nds.rate ? openDialog(item) : null"
                >
                  {{ isEnabled ? formatStartPrice(item.offerPrice) : formatStartPrice(item.offerPriceWithNds) }} $
                  <span>{{getIsNdsText(isEnabled ? null : item.nds.rate)}}</span>
                </span>
                <span
                    v-if="item.previous?.offerPrice === null || item.offerPrice === item.previous?.offerPrice"
                    class="offerPriceEmpty"
                >{{ $t('не улучшалась') }}</span>
                <span
                    v-else
                    :style="{ cursor: item.previous?.nds?.rate ? 'pointer' : 'default' }"
                    class="offerPricePrev"
                    @click="item.previous?.nds?.rate ? openDialog(item, true) : null"
                >
                  {{ isEnabled ? formatStartPrice(item.previous?.offerPrice) : formatStartPrice(item.previous?.offerPriceWithNds) }} $
                </span>
              </div>
            </div>
            <span v-else>
              <span
                class="offerPrice"
                :style="{ cursor: item.nds.rate ? 'pointer' : 'default' }"
                @click="item.nds.rate ? openDialog(item) : null"
              >
                {{ isEnabled ? formatStartPrice(item.offerPrice) : formatStartPrice(item.offerPriceWithNds) }} $
                <span class="offerNds">{{getIsNdsText(isEnabled ? null : item.nds.rate)}}</span>
              </span>
            </span>
          </template>
          <template v-slot:[`item.${selectedKey}`]="{ item }">
            <v-chip class="selectedStatus" :class="selectedChipClass(item[this.selectedKey])">
              <v-icon
                icon="custom:middleDotIcon"
                class="selectedDotIcon"
                :class="selectedChipDotClass(item[this.selectedKey])"
                start
              />
              {{ $t(getSelectedStatus(item)) }}
            </v-chip>
          </template>
          <template v-slot:item.createdAt="{ item }">
            <span>{{ formatDateTimeNoTz(item.createdAt) }}</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-icon v-if="item.hasFiles" size="large" class="mr-4" @click="downloadDocuments(item.id)">
              custom:downloadFilesIcon
            </v-icon>
            <v-icon size="large" class="mr-4" @click="select(item)">
              custom:thumbUpIcon
            </v-icon>
            <v-icon size="large" @click="unselect(item)">
              custom:thumbDownIcon
            </v-icon>
          </template>
        </DataTable>
      </v-col>
    </v-row>
  </Card>
</template>

<script>
import Card from "@intec/vue-components/form/Card.vue";
import { calcPriceNdsByPrice, getIsNdsText } from "@/lib/nds";
import { formatDateTimeNoTz, formatStartPrice } from '@/filters';
import ProcedureStep from "@/components/ProcedureStep";
import { computed, onMounted } from "vue";
import { useLibraryStore } from "@/stores/library";
import { useDecisionGridStore } from "@/stores/decisionGridStore";
import NdsDlg from "@/components/application/dialogs/NdsDlg.vue";
import { useDecisionGridActionsComponentStore} from "@/stores/decisionGridActionsComponentStore";
import DataTable from '@intec/vue-components/table/DataTable.vue'

const { REVIEW, SUMMING } = ProcedureStep;
const config = {
  [REVIEW]: {
    title: 'Рассмотрение заявок закупки',
    subtitle: 'Определите допуски заявок',
    companyKey: 'supplierName',
    selectedKey: 'isAccepted',
    selectedStatus: 'Допущена',
    isSingleSelection: false,
  },
  [SUMMING]: {
    title: 'Рассмотрение итогов закупки',
    subtitle: 'Определите победителя',
    companyKey: 'orgName',
    selectedKey: 'isWinner',
    selectedStatus: 'Победитель',
    isSingleSelection: true,
  },
};

export default {
  name: 'DecisionGrid',
  components: {Card, NdsDlg, DataTable},
  props: {
    status: {
      type: String,
      required: true,
      validator: value => [REVIEW, SUMMING].includes(value)
    },
    procedureType: {
      type: String,
      required: true
    },
    applications: {
      type: Array,
      required: true
    },
    isNds: {
      type: [Boolean, null],
      required: true
    },
    hasManyRounds: {
      type: Boolean,
      required: false
    },
    successful: {
      type: [Boolean, null],
      required: true
    },
    procedureId: {
      type: Number,
      required: true
    }
  },
  setup(props) {
    const decisionGridStore = useDecisionGridStore();
    const decisionGridActionsComponentStore = useDecisionGridActionsComponentStore();
    const libraryStore = useLibraryStore();

    const decisionGridActionsComponent = computed(() => {
      return decisionGridActionsComponentStore.getComponentByType(props.procedureType);
    });

    onMounted(() => {
      libraryStore.callActionByCode('initDecisionGridOptions', {
        procedureType: props.procedureType,
        procedureStatus: props.status
      });
    });

    return {
      decisionGridStore,
      decisionGridActionsComponent
    };
  },
  data() {
    return {
      isNdsDlgOpen: false,
      isEnabled: !this.isNds,
      selectedApplic: null,
      formatDateTimeNoTz,
      formatStartPrice,
      calcPriceNdsByPrice,
      getIsNdsText,
    }
  },
  computed: {
    title: function () {
      return this.status && config[this.status].title;
    },
    subtitle: function () {
      return this.status && config[this.status].subtitle;
    },
    companyKey: function () {
      return this.status && config[this.status].companyKey;
    },
    selectedKey: function () {
      return this.status && config[this.status].selectedKey;
    },
    selectedStatus: function () {
      return this.status && config[this.status].selectedStatus;
    },
    isSingleSelection: function () {
      return this.status && config[this.status].isSingleSelection;
    },
    headers: function () {
      const headersArray = [
        {
          title: this.$t('Наименование'),
          key: this.companyKey
        },
        {
          title: this.$t('Дата и время подачи'),
          key: 'createdAt',
          cellProps: { style: 'white-space: nowrap;' }
        },
      ];
      if (!this.decisionGridStore.hidePrice) {
        headersArray.push({
          title: this.$t('Предложение'),
          key: 'offerPrice',
          cellProps: { style: 'white-space: nowrap;' }
        });
      }
      headersArray.push(
          {
            title: this.$t('Статус'),
            key: this.selectedKey,
            cellProps: { style: 'white-space: nowrap;' }
          },
          {
            title: '',
            key: 'actions',
            align: 'end',
            cellProps: { style: 'white-space: nowrap;' }
          }
      );
      return headersArray;
    },
  },
  methods: {
    async downloadDocuments(applicationId) {
      window.location.href = '/procedures/downloadAllDocumentsApplication/' + this.procedureId +
        `?applicationId=${applicationId}`;
    },
    select(item) {
      if (this.isSingleSelection) {
        this.setAllTo(false);
      }
      item[this.selectedKey] = true;
    },
    unselect(item) {
      item[this.selectedKey] = false;
    },
    setAllTo(withValue = null) {
      this.applications.forEach(application => {
        application[this.selectedKey] = withValue;
      });
    },
    getSelectedStatus(item) {
      if (item[this.selectedKey] === true) {
        return this.selectedStatus;
      }
      if (item[this.selectedKey] === false) {
        return 'Отклонена';
      }
      return 'Не рассмотрена';
    },
    selectedChipClass(selected) {
      return {
        'notReviewedChip': selected === null,
        'selectedChip': selected === true,
        'unselectedChip': selected === false,
      }
    },
    selectedChipDotClass(selected) {
      return {
        'notReviewedChipDot': selected === null,
        'selectedChipDot': selected === true,
        'unselectedChipDot': selected === false,
      }
    },
    openDialog(applic, prev = false) {
      this.isNdsDlgOpen = true;
      this.selectedApplic = prev ? applic.previous : applic;
    }
  },
  mounted: function () {
    this.setAllTo();

    const libraryStore = useLibraryStore();
    // Даем внешнему модулю шанс выбрать заявку.
    const minApplication = libraryStore.callFunctionByCode('autoSelectApplication', {
      procedureType: this.procedureType,
      applications: this.applications,
      status: this.status,
      select: this.select
    }).minApplication ?? false;

    if (minApplication) {
      this.select(minApplication);
    }
  }
}
</script>

<style lang="scss" scoped>
.cardTitle {
  color: var(--black-color);
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.resetButton {
  height: 29px !important;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: normal;
}

.v-selection-control {
  font-family: var(--primary-font-family-name), sans-serif;
  font-weight: 500;
  color: #162040;
  line-height: 2;
  margin-left: -5px;
}

.applicationsGrid {
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;

  .offerPriceArrow {
    width: 22px;
  }

  .offerPrice {
    font-weight: 500;
  }

  .offerNds {
    font-weight: 400;
  }

  .offerPricePrev {
    text-decoration-line: line-through;
    font-size: 14px;
    color: rgb(var(--unactive-text-color));
  }

  .offerPriceEmpty {
    font-size: 14px;
    color: rgb(var(--unactive-text-color));
  }

  :deep(thead th) {
    color: rgb(var(--table-thead-font-color));
    background-color: rgb(var(--table-thead-background-color));
    border-color: rgb(var(--table-border-color));
    border-top: 1px solid rgb(var(--table-border-color));
  }

  :deep(thead th:first-child) {
    border-top-left-radius: 10px;
    border-left: 1px solid rgb(var(--table-border-color));
  }

  :deep(thead th:last-child) {
    border-top-right-radius: 10px;
    border-right: 1px solid rgb(var(--table-border-color));
  }

  :deep(tbody) {
    font-size: 16px;
  }

  :deep(tbody tr td:first-child) {
    border-left: 1px solid rgb(var(--table-border-color));
  }

  :deep(tr:last-child  td) {
    border-bottom: 1px solid rgb(var(--table-border-color));
  }

  :deep(tbody tr td:last-child) {
    border-right: 1px solid rgb(var(--table-border-color));
  }

  :deep(tbody tr:last-child td:first-child) {
    border-bottom-left-radius: 10px;
  }

  :deep(tbody tr:last-child td:last-child) {
    border-bottom-right-radius: 10px;
  }

  :deep(table) {
    table-layout: auto !important;
  }

  :deep(th),
  :deep(td) {
    white-space: nowrap;
  }

  :deep(th:nth-child(1)),
  :deep(td:nth-child(1)) {
    white-space: normal;
    width: 87%;
  }

  .selectedStatus {
    padding: 2px 9px;
    height: auto;
    font-size: 14px;
  }

  .selectedChip {
    background-color: rgb(var(--accepted-color));
  }

  .unselectedChip {
    background-color: rgb(var(--declined-color));
  }

  .notReviewedChip {
    background-color: rgb(var(--not-reviewed-color));
  }

  :deep(svg.selectedDotIcon circle) {
    r: 2;
  }

  :deep(svg.selectedChipDot circle) {
    fill: rgb(var(--accepted-dot-color));
  }

  :deep(svg.unselectedChipDot circle) {
    fill: rgb(var(--declined-dot-color));
  }

  :deep(svg.notReviewedChipDot circle) {
    fill: rgb(var(--not-reviewed-dot-color));
  }
}

/* v-switch */
:deep(.v-input--density-default) {
  --v-input-control-height: 28px;
}

:deep(.v-selection-control--density-default) {
  --v-selection-control-size: 28px;
}

:deep(.v-switch .v-label) {
  color: rgb(var(--v-theme-primary));
  opacity: 1;
  font-family: var(--primary-font-family-name);
  font-weight: 500;
  font-size: 16px;
}

:deep(.v-switch .v-selection-control) {
  max-height: var(--v-input-control-height);
  flex-direction: row-reverse;
  gap: 10px;
}

:deep(.v-switch .v-switch__track) {
  height: 28px;
  opacity: 1;
}

:deep(.v-switch .v-selection-control.v-selection-control--dirty .v-switch__track) {
  background-color: var(--blue-blue-hover);
}

:deep(.v-switch .v-selection-control:not(.v-selection-control--dirty) .v-switch__track) {
  background-color: #c7c7c7;
}

:deep(.v-switch .v-switch__thumb) {
  transform: none;
}
</style>